import { sortBy } from "lodash-es";
import { defineStore } from "pinia";

import type { Sticker } from "@/model/StickerType";
import type { Color, IdMap, InfoLevel, StatusClass } from "@/model/baseTypes";
import { statusClasses } from "@/model/baseTypes";
import type { BoardCard, LinkedSticky } from "@/model/card";
import { statusClassColors } from "@/model/colors";
import type { Link } from "@/model/link";
import type { DependencyTeamFilter, DependencyTeamItem } from "@/model/search";
import type { Art, Iteration, Team } from "@/model/session";
import type { StickyType } from "@/model/stickyType";
import { isDependency } from "@/model/stickyType";
import type { AuthUser } from "@/model/user";
import { useGlobalI18n } from "@/translations/i18n";
import { toCssColor, white } from "@/utils/color";
import { parseStickerString, stickerToFilterKey } from "@/utils/sticker";
import type { FilterProps } from "@/utils/type";

import { NO_ART, useArtStore } from "../art";
import { useBoardStore } from "../board";
import { useSessionStore } from "../session";
import { NO_TEAM, isNotUserTeam, useTeamStore } from "../team";
import { noAssignee, noReporter, useUserStore } from "../user";

export const dependencyLinks = ["Linked", "Not Linked"];
export type DependencyLink = (typeof dependencyLinks)[number];

const { t } = useGlobalI18n();

interface DependencyLinkItem {
  id: DependencyLink;
  name: string;
}

export const dependencyLinkItems: DependencyLinkItem[] = [
  { id: "Linked", name: /*$t*/ "dependency.linked" },
  { id: "Not Linked", name: /*$t*/ "dependency.notLinked" },
];

export type UpperLevelLinksSelection =
  | { type: "none" }
  | { type: "linked" }
  | { type: "unlinked" }
  | { type: "sticky"; stickyId: string };

type StatusDistributionSelection =
  | { type: "none" }
  | {
      type: "statusClass";
      statusClass: StatusClass;
      stickyIds: string[];
      parentStickyId?: string;
    };

export interface StatusItem {
  id: string;
  statusClass: StatusClass;
  name: string;
  color: Color | string;
  order: number;
}

export interface SearchMenuState {
  isSearchOpen: boolean; // Whether the search is open
}

export interface SearchState {
  text: string;
  selectedStickyTypeIds: Set<string>;
  selectedDependencyLink: Set<DependencyLink>;
  selectedLinkStates: Set<InfoLevel>;
  selectedAssigneeIds: Set<string>;
  selectedReporterIds: Set<string>;
  selectedTeamIds: Set<string>;
  selectedArtIds: Set<string>;
  selectedIterationIds: Set<number>;
  selectedFlagStrings: Set<string>;
  selectedStatusIds: Set<string>;
  selectedDependencyTeamFilter: DependencyTeamFilter;
  selectedUpperLevelLinks: UpperLevelLinksSelection;
  selectedStatusDistribution: StatusDistributionSelection;
}

export type UrlSyncedSearchState = Omit<
  SearchState,
  "selectedDependencyTeamFilter" | "selectedStatusDistribution"
>;

/** properties that hold a Set of filter values. */
export const setSearchProps = [
  "selectedStickyTypeIds",
  "selectedDependencyLink",
  "selectedLinkStates",
  "selectedAssigneeIds",
  "selectedReporterIds",
  "selectedTeamIds",
  "selectedArtIds",
  "selectedIterationIds",
  "selectedFlagStrings",
  "selectedStatusIds",
] satisfies Array<FilterProps<SearchState, Set<unknown>>>;

export const useSearchMenuStore = defineStore("searchMenu", {
  state: (): SearchMenuState & SearchState => ({
    isSearchOpen: false,
    ...emptySearchState(),
  }),
  getters: {
    selectedRiskyLinks(state): InfoLevel[] {
      return Array.from(state.selectedLinkStates);
    },
    selectedStickyTypes(state): StickyType[] {
      return useBoardStore().possibleStickyTypes.filter((type) =>
        state.selectedStickyTypeIds.has(type.id),
      );
    },
    selectedDependencyLinks(state): DependencyLink[] {
      return Array.from(state.selectedDependencyLink);
    },
    selectedDependencyLinkItems(state) {
      return dependencyLinkItems.filter((item) => state.selectedDependencyLink.has(item.id));
    },
    isDependencySearch(state): boolean {
      return useBoardStore().possibleStickyTypes.some(
        (stickyType) =>
          stickyType.functionality === "dependency" &&
          state.selectedStickyTypeIds.has(stickyType.id),
      );
    },
    allUsers(): { assignee: AuthUser[]; reporter: AuthUser[] } {
      const res: { assignee: IdMap<AuthUser>; reporter: IdMap<AuthUser> } = {
        assignee: {},
        reporter: {},
      };
      const cards = useBoardStore().currentBoard().cards;
      for (const type of ["assignee", "reporter"] as const) {
        for (const id in cards) {
          const user = cards[id].data[type];
          if (user) {
            res[type][user.id] = user;
          }
        }
        // Add the current user to the list
        // (so they can confirm they have no cards assigned/created)
        const currentUser = useUserStore().currentUser;
        if (currentUser) {
          res[type][currentUser.id] ??= currentUser;
        }
      }
      return {
        assignee: [...Object.values(res.assignee), noAssignee(toCssColor(white))],
        reporter: [...Object.values(res.reporter), noReporter(toCssColor(white))],
      };
    },
    allAssignees(): AuthUser[] {
      return this.allUsers.assignee;
    },
    selectedAssignees(state): AuthUser[] {
      return this.allAssignees.filter((assignee) => state.selectedAssigneeIds.has(assignee.id));
    },
    allReporters(): AuthUser[] {
      return this.allUsers.reporter;
    },
    selectedReporters(state): AuthUser[] {
      return this.allReporters.filter((reporter) => state.selectedReporterIds.has(reporter.id));
    },
    allTeams(): Team[] {
      return [...sortBy(useTeamStore().teamsInCurrentArt, isNotUserTeam), useTeamStore().noTeam];
    },
    selectedTeams(state): Team[] {
      return this.allTeams.filter((team) => state.selectedTeamIds.has(team.id));
    },
    effectiveTeamIds(): Set<string> {
      return useBoardStore().currentBoard().type === "solution"
        ? this.teamIdsOfSelectedArts
        : this.selectedTeamIds;
    },
    teamIdsOfSelectedArts(state): Set<string> {
      return new Set(
        useTeamStore()
          .teams.filter((team) => state.selectedArtIds.has(team.artId || ""))
          .map((team) => team.id),
      );
    },
    isTeamSelected() {
      return (teamId: Team["id"] | undefined) => this.effectiveTeamIds.has(teamId || NO_TEAM.id);
    },
    allArts(_state): Art[] {
      return [...useArtStore().arts, useArtStore().noArt];
    },
    selectedArts(state): Art[] {
      return this.allArts.filter((art) => state.selectedArtIds.has(art.id));
    },
    isArtSelected() {
      return (artId: Art["id"] | undefined) => this.selectedArtIds.has(artId || NO_ART.id);
    },
    flags(state): Array<Sticker | null> {
      return Array.from(state.selectedFlagStrings).map(parseStickerString);
    },
    allStatusItems(): StatusItem[] {
      const cards = useBoardStore().currentBoard().cards;

      // Collect all statuses with duplicates
      const all = Object.values(cards).flatMap((card) => {
        const s = card.data.status;
        if (!s) return [];
        return [
          {
            id: `${s.statusClass}-${s.name}`,
            statusClass: s.statusClass,
            name: s.name,
            color: statusClassColors[s.statusClass],
            order: s.order,
          },
        ];
      });

      // Deduplicate by `id`
      const uniqueMap = new Map(all.map((s) => [s.id, s]));
      const unique = Array.from(uniqueMap.values());

      // Two-level sorting to handle a mix of ALM and local status classes
      // (ALM-defined status classes have an order, local ones don't)
      return sortBy(unique, [
        (item) => statusClasses.indexOf(item.statusClass), // First group/sort by status class
        (item) => item.order, // Then follow backend-defined order within each class
      ]);
    },
    selectedStatusItems(state): StatusItem[] {
      return this.allStatusItems.filter((item) => state.selectedStatusIds.has(item.id));
    },
    allIterations(): Iteration[] {
      return useSessionStore().iterations;
    },
    selectedIterations(state): Iteration[] {
      return this.allIterations.filter((iteration) => state.selectedIterationIds.has(iteration.id));
    },
    hasUpperLevelLinksFilter(state): boolean {
      return state.selectedUpperLevelLinks.type !== "none";
    },
    hasStatusDistributionFilter(state): boolean {
      return state.selectedStatusDistribution.type !== "none";
    },
    anyFilterCriteria(state): boolean {
      return (
        state.text !== "" ||
        setSearchProps.some((k) => state[k].size !== 0) ||
        this.hasUpperLevelLinksFilter ||
        this.hasStatusDistributionFilter
      );
    },
    dependencyFilterItems(state): DependencyTeamItem[] {
      return [
        {
          id: "all",
          name: t("depTeamFilter.all"),
          description: t("depTeamFilter.all.desc", { count: state.selectedTeamIds.size }),
        },
        {
          id: "blocked",
          name: t("depTeamFilter.blocked"),
          description: t("depTeamFilter.blocked.desc", { count: state.selectedTeamIds.size }),
        },
        {
          id: "enabling",
          name: t("depTeamFilter.enabling"),
          description: t("depTeamFilter.enabling.desc", { count: state.selectedTeamIds.size }),
        },
        ...(state.selectedTeamIds.size < 2
          ? []
          : [
              {
                id: "mutual" as const,
                name: t("depTeamFilter.mutual"),
                description: t("depTeamFilter.mutual.desc"),
              },
            ]),
      ];
    },
    selectedDependencyFilterItem(state): DependencyTeamItem {
      return this.dependencyFilterItems.find(
        (item) => item.id === state.selectedDependencyTeamFilter,
      )!;
    },
    showDependencyTeamFilter(state): boolean {
      return this.isDependencySearch && state.selectedTeamIds.size > 0;
    },
    matchesLinkStates:
      (state) =>
      (link: LinkedSticky | Link, emptyMatch = false) => {
        const states = state.selectedLinkStates;
        return (emptyMatch && states.size === 0) || Array.from(states).some(matchesLinkState);

        function matchesLinkState(linkState: InfoLevel) {
          switch (linkState) {
            case "default":
              return true;
            case "warn":
              return link.state === "warn";
            case "error":
              return link.state === "error";
          }
        }
      },
    showCardLinks() {
      return (card: BoardCard) => {
        const acid = useBoardStore().activeCardId;
        const showForSelectedCard = acid === card.data.id;

        return (
          (isDependency(card.data) && this.isDependencySearch) ||
          showForSelectedCard ||
          card.meta.dragging ||
          card.meta.isHighlighted ||
          card.meta.isRelatedToHighlighted
        );
      };
    },
  },
  actions: {
    expandSearch() {
      this.isSearchOpen = true;
    },
    collapseSearch() {
      this.isSearchOpen = false;
    },
    toggleSearch() {
      if (this.isSearchOpen) {
        this.collapseSearch();
      } else {
        this.expandSearch();
      }
    },
    resetCriteria() {
      Object.assign(this, emptySearchState());
    },
    toggleFlag(flag: Sticker | null) {
      const flagString = stickerToFilterKey(flag);
      if (this.selectedFlagStrings.has(flagString)) {
        this.selectedFlagStrings.delete(flagString);
      } else {
        this.selectedFlagStrings.add(flagString);
      }
      this.selectedFlagStrings = new Set(this.selectedFlagStrings);
    },
    clearFlags() {
      this.selectedFlagStrings = new Set();
    },
    // Filter setters
    selectIterations(iterations: Iteration[]) {
      this.selectedIterationIds = new Set(iterations.map((i) => i.id));
    },
    selectStickyTypes(stickyTypes: StickyType[]) {
      this.selectedStickyTypeIds = new Set(stickyTypes.map((t) => t.id));
    },
  },
});

function emptySearchState() {
  return {
    text: "",
    ...Object.fromEntries(setSearchProps.map((k) => [k, new Set()])),
    selectedDependencyTeamFilter: "all",
    selectedUpperLevelLinks: { type: "none" },
    selectedStatusDistribution: { type: "none" },
  } as SearchState;
}
